import { RefreshCw, Trash2, Search, Loader2, Tags } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PipelineStepper } from './PipelineStepper'
import { TaxonomyReviewView } from './TaxonomyReviewView'
import { EntityGraph } from './EntityGraph'
import { StatsView } from './StatsView'
import { ReconciliationView } from './ReconciliationView'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TaxonomyRun, TaxonomyIntent, GraphData } from '@/hooks/useTaxonomyRun'
import { useLatestReconciliation, useReconciliationRun, useReconciliationStatus } from '@/hooks/useReconciliation'
import { useExportTaxonomyAsTagset } from '@/hooks/useTagsets'
import { useToast } from '@/hooks/use-toast'

interface CompletedStateProps {
  run: TaxonomyRun
  intents: TaxonomyIntent[]
  graphData: GraphData
  kbId: string
  onReRun: () => void
  isReRunning?: boolean
  onDelete?: (runId: string) => void
  isDeleting?: boolean
}

export function CompletedState({ run, intents, graphData, kbId, onReRun, isReRunning, onDelete, isDeleting }: CompletedStateProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [exportName, setExportName] = useState('')
  const [activeReconId, setActiveReconId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { toast } = useToast()
  const exportTagset = useExportTaxonomyAsTagset()

  const latestRecon = useLatestReconciliation(kbId)
  const triggerRecon = useReconciliationRun(kbId)

  // Use the actively-tracked ID (from mutation) or fall back to latest query
  const reconId = activeReconId ?? latestRecon.data?.reconciliation_id ?? null
  const hasRecon = !!reconId

  // Poll the active reconciliation so we get fresh status after re-runs
  const { data: reconStatusData } = useReconciliationStatus(kbId, reconId)
  const reconFinalized = reconStatusData?.finalized ?? latestRecon.data?.finalized ?? false
  const reconProcessing = reconStatusData?.status === 'processing'

  const handleReconciliation = async () => {
    try {
      const result = await triggerRecon.mutateAsync()
      // Immediately switch to the new reconciliation ID
      if (result?.reconciliation_id) {
        setActiveReconId(result.reconciliation_id)
      }
      toast({ title: 'Reconciliation started', description: 'Analyzing intents for semantic duplicates...' })
    } catch (err: any) {
      toast({ title: 'Failed to start reconciliation', description: err?.response?.data?.error || 'Something went wrong.', variant: 'destructive' })
    }
  }

  // Format "Last run: X ago" relative time
  const completedAt = run.completed_at ? new Date(run.completed_at) : null
  const timeAgo = completedAt
    ? formatTimeAgo(completedAt)
    : ''

  return (
    <div className="w-full">
      {/* Top bar: stepper (all green) + re-run button + timestamp */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <PipelineStepper progress={100} status="completed" />
        </div>
      </div>

      {/* Re-run row */}
      <div className="flex items-center justify-between mt-2 mb-4 px-2">
        <span className="text-xs text-gray-400">
          {run.intents_discovered} intents discovered · {run.total_chunks_processed} chunks processed
        </span>
        <div className="flex items-center gap-3">
          {timeAgo && <span className="text-xs text-gray-400">Last run: {timeAgo}</span>}
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowDeleteConfirm(true)} 
              disabled={isDeleting || isReRunning} 
              variant="outline" 
              size="sm" 
              className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              onClick={() => {
                setExportName(`GenClair - ${new Date().toLocaleDateString()}`)
                setShowExportDialog(true)
              }}
              disabled={exportTagset.isPending}
              variant="outline"
              size="sm"
              className="gap-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
            >
              <Tags className="w-3.5 h-3.5" />
              {exportTagset.isPending ? 'Exporting…' : 'Export as Tagset'}
            </Button>
            <Button 
              onClick={onReRun} 
              disabled={isReRunning || isDeleting} 
              variant="outline" 
              size="sm" 
              className="gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isReRunning ? 'animate-spin' : ''}`} />
              {isReRunning ? 'Starting…' : 'Re-run'}
            </Button>
          </div>
        </div>
        
        {/* Export as Tagset Dialog */}
        <AlertDialog open={showExportDialog} onOpenChange={setShowExportDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Tags className="w-5 h-5 text-purple-600" />
                Export as Tagset
              </AlertDialogTitle>
              <AlertDialogDescription>
                Generate a RAGflow-compatible tagset from this taxonomy's {intents.length} intents.
                The tagset will be created, uploaded, and parsed automatically.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <Label htmlFor="export-name">Tagset Name</Label>
              <Input
                id="export-name"
                value={exportName}
                onChange={(e) => setExportName(e.target.value)}
                placeholder="e.g., GenClair - My KB"
                className="mt-1"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={exportTagset.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={exportTagset.isPending || !exportName.trim()}
                onClick={async () => {
                  try {
                    const result = await exportTagset.mutateAsync({
                      kbId,
                      runId: run.run_id,
                      name: exportName.trim(),
                    })
                    toast({
                      title: 'Tagset exported',
                      description: result.message || 'Tagset created and parsing started.',
                    })
                    setShowExportDialog(false)
                    // Navigate to the new tagset
                    if (result.tagset?.id) {
                      navigate(`/app/tagsets/${result.tagset.id}`)
                    }
                  } catch (err: any) {
                    toast({
                      title: 'Export failed',
                      description: err?.response?.data?.error || err.message,
                      variant: 'destructive',
                    })
                  }
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {exportTagset.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Exporting...</>
                ) : (
                  'Export'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete taxonomy data</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the current taxonomy and all extracted metadata. 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                disabled={isDeleting}
                onClick={() => {
                  if (onDelete) onDelete(run.run_id);
                  setShowDeleteConfirm(false);
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Sub-navigation: Taxonomy | Entity Graph | Stats | Reconciliation */}
      <Tabs defaultValue="taxonomy">
        <TabsList className="w-full">
          <TabsTrigger value="taxonomy" className="flex-1">Taxonomy</TabsTrigger>
          <TabsTrigger value="graph" className="flex-1">Entity Graph</TabsTrigger>
          <TabsTrigger value="stats" className="flex-1">Stats</TabsTrigger>
          <TabsTrigger value="reconciliation" className="flex-1 gap-1.5">
            Reconciliation
            {hasRecon && !reconFinalized && (reconStatusData?.summary?.pending_review ?? 0) > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                {reconStatusData?.summary?.pending_review}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="taxonomy">
          <TaxonomyReviewView intents={intents} kbId={kbId} runId={run.run_id} />
        </TabsContent>

        <TabsContent value="graph">
          <EntityGraph graphData={graphData} />
        </TabsContent>

        <TabsContent value="stats">
          <StatsView intents={intents} graphData={graphData} />
        </TabsContent>

        <TabsContent value="reconciliation">
          {hasRecon && reconId ? (
            <div className="space-y-3 pt-2">
              {!reconFinalized && !reconProcessing && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={handleReconciliation}
                    disabled={triggerRecon.isPending}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${triggerRecon.isPending ? 'animate-spin' : ''}`} />
                    {triggerRecon.isPending ? 'Starting...' : 'Re-run Reconciliation'}
                  </Button>
                </div>
              )}
              <ReconciliationView kbId={kbId} reconciliationId={reconId} />
            </div>
          ) : (
            <div className="py-12 text-center space-y-4">
              <Search className="w-10 h-10 text-muted-foreground mx-auto" />
              <div>
                <h3 className="font-semibold">Find Duplicate Intents</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Use AI to analyze your {intents.length} intents for semantic duplicates
                  and suggest merges to optimize your taxonomy.
                </p>
              </div>
              <Button
                onClick={handleReconciliation}
                disabled={triggerRecon.isPending}
                className="gap-2"
              >
                {triggerRecon.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Starting...</>
                ) : (
                  <><Search className="w-4 h-4" /> Analyze for Duplicates</>
                )}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ── Utility ──────────────────────────────────────────────────────────
function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60)  return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago` 
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago` 
  return `${Math.floor(seconds / 86400)}d ago` 
}
